import React, { useState, useEffect } from 'react';
import { DollarSign, ArrowDownCircle, Clock, CheckCircle, ExternalLink, Wallet, Building, Zap, TrendingUp, RefreshCw } from 'lucide-react';

const API = 'https://api.naijapredict.net';
const T = { bg2:'#0d1114', border:'rgba(255,255,255,.06)', muted:'#4a5a63', green:'#00c566', yellow:'#f5c842' };

export default function Payouts() {
  const [tab, setTab]           = useState('overview');
  const [method, setMethod]     = useState('usdc');
  const [loading, setLoading]   = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [royalties, setRoyalties] = useState(null);
  const [address, setAddress]   = useState('');

  useEffect(() => { fetchRoyalties(); }, []);

  async function fetchRoyalties() {
    setLoading(true);
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API + '/api/creator/royalties', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await res.json();
      setRoyalties(data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function withdraw() {
    if (!address) return alert('Enter wallet address');
    if (parseFloat(royalties?.totalAccrued||0) < 1) return alert('Minimum $1 USDC to withdraw');
    setWithdrawing(true);
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API + '/api/dollar/withdraw', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify({ toAddress: address, usdcAmount: royalties.totalAccrued })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert('Withdrawal sent! TX: ' + data.txHash);
      fetchRoyalties();
    } catch(e) { alert('Error: ' + e.message); }
    finally { setWithdrawing(false); }
  }

  const stateLabel = {0:'Active',1:'Resolved',2:'Cancelled'};
  const stateColor = {0:T.green,1:T.yellow,2:'#ff4444'};

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4, flexWrap:'wrap', gap:10 }}>
        <h1 style={{ fontSize:'clamp(20px,4vw,28px)', fontWeight:800 }}>Royalties & Payouts</h1>
        <button onClick={fetchRoyalties} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:`1px solid ${T.border}`, background:'rgba(255,255,255,.04)', color:T.muted, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
          <RefreshCw size={13}/> Refresh
        </button>
      </div>
      <p style={{ fontSize:14, color:T.muted, marginBottom:20 }}>Earn 0.5% royalties on every trade across your markets</p>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
        {[
          [Zap,         'Accrued (Pending)', '$'+(loading?'...':parseFloat(royalties?.totalAccrued||0).toFixed(4)), T.green],
          [CheckCircle, 'Total Paid Out',    '$'+(loading?'...':parseFloat(royalties?.totalPaid||0).toFixed(4)),    T.yellow],
          [TrendingUp,  'Active Markets',    loading?'...':(royalties?.markets?.filter(m=>m.state===0).length||0),  T.green],
          [DollarSign,  'Total Markets',     loading?'...':royalties?.totalMarkets||0,                              T.muted],
        ].map(([Icon,label,val,color])=>(
          <div key={label} style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:16 }}>
            <Icon size={16} color={color} style={{ marginBottom:10 }}/>
            <div style={{ fontSize:22, fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{ background:'rgba(0,197,102,.04)', border:'1px solid rgba(0,197,102,.12)', borderRadius:14, padding:16, marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.green, marginBottom:8 }}>How Creator Royalties Work</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, fontSize:12, color:T.muted }}>
          {[
            ['0.5%','Of every trade on your market'],
            ['Auto-paid','On market resolution'],
            ['On-chain','Smart contract guarantees payment'],
            ['No lock','Withdraw anytime after resolution'],
          ].map(([v,d])=>(
            <div key={v} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
              <div style={{ fontSize:15, fontWeight:800, color:T.green, flexShrink:0 }}>{v}</div>
              <div>{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[['overview','My Markets'],['withdraw','Withdraw']].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:'8px 20px', borderRadius:8, border:`1px solid ${tab===id?'rgba(0,197,102,.3)':T.border}`, background:tab===id?'rgba(0,197,102,.08)':'rgba(255,255,255,.03)', color:tab===id?T.green:T.muted, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' }}>{label}</button>
        ))}
      </div>

      {/* Markets Tab */}
      {tab==='overview' && (
        <div>
          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:T.muted }}>Loading royalty data...</div>
          ) : !royalties?.markets?.length ? (
            <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:40, textAlign:'center', color:T.muted }}>
              <TrendingUp size={28} style={{ marginBottom:12, opacity:.4 }}/>
              <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>No markets yet</div>
              <div style={{ fontSize:13 }}>Create your first market to start earning royalties</div>
            </div>
          ) : royalties.markets.map(m=>(
            <div key={m.id} style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:18, marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10, gap:12 }}>
                <div style={{ fontSize:14, fontWeight:600, lineHeight:1.5, flex:1 }}>{m.question}</div>
                <span style={{ fontSize:11, color:stateColor[m.state], fontWeight:600, flexShrink:0 }}>{stateLabel[m.state]}</span>
              </div>
              <div style={{ height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden', marginBottom:10 }}>
                <div style={{ height:'100%', width:m.yesPct+'%', background:'linear-gradient(90deg,#00c566,#f5c842)', borderRadius:3 }}/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:8 }}>
                {[
                  ['Pool Volume', '$'+m.volume+' USDC'],
                  ['Royalty (0.5%)', '$'+m.estimatedRoyalty],
                  ['Accrued', '$'+m.creatorFeeAccrued],
                  ['Paid Out', m.state===1?'$'+m.creatorFeePaid:'On resolution'],
                ].map(([k,v])=>(
                  <div key={k} style={{ background:'rgba(255,255,255,.03)', borderRadius:8, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:T.muted, marginBottom:4 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:k==='Accrued'?T.green:k==='Paid Out'&&m.state===1?T.yellow:'#e8eaeb' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Withdraw Tab */}
      {tab==='withdraw' && (
        <div style={{ maxWidth:480 }}>
          <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:20, marginBottom:14 }}>
            <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>Available to Withdraw</div>
            <div style={{ fontSize:28, fontWeight:800, color:T.green, marginBottom:4 }}>${parseFloat(royalties?.totalAccrued||0).toFixed(4)} USDC</div>
            <div style={{ fontSize:12, color:T.muted }}>Accrued from {royalties?.totalMarkets||0} markets</div>
          </div>

          <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:20, marginBottom:14 }}>
            <div style={{ fontSize:14, fontWeight:700, marginBottom:14, display:'flex', alignItems:'center', gap:6 }}>
              <Wallet size={15} color={T.green}/> Withdrawal Method
            </div>
            {[
              {id:'usdc',Icon:Wallet,   title:'USDC Wallet',       desc:'Send to Ethereum wallet address'},
              {id:'bank',Icon:Building, title:'Nigerian Bank (NGN)',desc:'Via Flutterwave — coming soon'},
            ].map(({id,Icon,title,desc})=>(
              <div key={id} onClick={()=>setMethod(id)} style={{ display:'flex', alignItems:'center', gap:12, padding:14, borderRadius:12, border:`1px solid ${method===id?'rgba(0,197,102,.3)':T.border}`, background:method===id?'rgba(0,197,102,.04)':'rgba(255,255,255,.02)', cursor:'pointer', marginBottom:10, opacity:id==='bank'?.6:1 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:method===id?'rgba(0,197,102,.1)':'rgba(255,255,255,.04)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={17} color={method===id?T.green:T.muted}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:method===id?T.green:'#e8eaeb' }}>{title}</div>
                  <div style={{ fontSize:12, color:T.muted }}>{desc}</div>
                </div>
                {id==='bank'&&<span style={{ fontSize:10, color:T.muted, background:'rgba(255,255,255,.06)', padding:'2px 6px', borderRadius:4 }}>Soon</span>}
              </div>
            ))}
          </div>

          {method==='usdc' && (
            <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:20, marginBottom:14 }}>
              <label style={{ display:'block', fontSize:12, color:T.muted, fontWeight:600, letterSpacing:.5, marginBottom:6 }}>USDC WALLET ADDRESS (ETHEREUM)</label>
              <input value={address} onChange={e=>setAddress(e.target.value)} placeholder="0x..." style={{ background:'rgba(255,255,255,.04)', border:`1px solid ${T.border}`, borderRadius:10, padding:'12px 14px', color:'#e8eaeb', fontSize:14, width:'100%', outline:'none', fontFamily:'monospace' }}/>
            </div>
          )}

          <div style={{ background:'rgba(255,255,255,.02)', border:`1px solid ${T.border}`, borderRadius:16, padding:16, marginBottom:16 }}>
            {[['Available','$'+parseFloat(royalties?.totalAccrued||0).toFixed(4)+' USDC'],['Fee (1%)','$'+Math.min(5,Math.max(0.5,parseFloat(royalties?.totalAccrued||0)*0.01)).toFixed(4)],['You Receive','$'+(parseFloat(royalties?.totalAccrued||0)-Math.min(5,Math.max(0.5,parseFloat(royalties?.totalAccrued||0)*0.01))).toFixed(4)+' USDC']].map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 0', borderBottom:`1px solid ${T.border}` }}>
                <span style={{ color:T.muted }}>{k}</span>
                <span style={{ fontWeight:600, color:k==='You Receive'?T.green:'#e8eaeb' }}>{v}</span>
              </div>
            ))}
          </div>

          <button onClick={withdraw} disabled={withdrawing||parseFloat(royalties?.totalAccrued||0)<1||!address}
            style={{ width:'100%', padding:14, borderRadius:12, border:'none', background:'linear-gradient(135deg,#00c566,#00a854)', color:'#000', fontWeight:700, fontSize:15, cursor:'pointer', fontFamily:'inherit', opacity:parseFloat(royalties?.totalAccrued||0)<1||!address?.5:1 }}>
            {withdrawing?'Processing...':'Withdraw $'+parseFloat(royalties?.totalAccrued||0).toFixed(4)+' USDC'}
          </button>
        </div>
      )}
    </div>
  );
}
